// Activity Feed Component - Shows chronological list of client activities
import React from 'react';
import {
  MessageCircle,
  Calendar,
  FileText,
  AlertTriangle,
  Mail,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Heart,
  User,
  ChevronRight
} from 'lucide-react';
import { ActivityLog, ActivityType, ActivityPriority } from '../../types/activity';

interface ActivityFeedProps {
  activities: ActivityLog[];
  onSelectActivity: (activity: ActivityLog) => void;
  onMarkAsRead: (activityId: string) => void;
  loading?: boolean;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({
  activities,
  onSelectActivity,
  onMarkAsRead,
  loading = false
}) => {
  // Get icon for activity type
  const getActivityIcon = (type: ActivityType) => {
    switch (type) {
      case 'hope_conversation':
        return <Heart size={18} />;
      case 'appointment_scheduled':
        return <Calendar size={18} />;
      case 'appointment_cancelled':
        return <XCircle size={18} />;
      case 'appointment_completed':
        return <CheckCircle size={18} />;
      case 'form_submitted':
        return <FileText size={18} />;
      case 'message_sent':
      case 'message_received':
        return <Mail size={18} />;
      case 'resource_viewed':
        return <Eye size={18} />;
      case 'crisis_detected':
        return <AlertTriangle size={18} />;
      case 'login':
        return <User size={18} />;
      case 'check_in':
        return <Clock size={18} />;
      case 'emergency_cancellation':
        return <AlertTriangle size={18} />;
      default:
        return <MessageCircle size={18} />;
    }
  };

  // Get color based on priority
  const getPriorityColor = (priority: ActivityPriority) => {
    switch (priority) {
      case 'crisis':
        return '#dc3545'; // Red
      case 'high':
        return '#fd7e14'; // Orange
      case 'moderate':
        return '#ffc107'; // Yellow
      case 'routine':
        return '#64748b'; // Gray
      default:
        return '#64748b';
    }
  };

  // Get background color for priority indicator
  const getPriorityBgColor = (priority: ActivityPriority) => {
    switch (priority) {
      case 'crisis':
        return 'rgba(220, 53, 69, 0.15)';
      case 'high':
        return 'rgba(253, 126, 20, 0.15)';
      case 'moderate':
        return 'rgba(255, 193, 7, 0.15)';
      case 'routine':
        return 'rgba(100, 116, 139, 0.1)';
      default:
        return 'rgba(100, 116, 139, 0.1)';
    }
  };

  // Format timestamp
  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Group activities by date
  const groupActivitiesByDate = (activities: ActivityLog[]) => {
    const groups: { [key: string]: ActivityLog[] } = {};

    activities.forEach((activity) => {
      const date = new Date(activity.timestamp);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      let dateKey: string;

      if (date.toDateString() === today.toDateString()) {
        dateKey = 'Today';
      } else if (date.toDateString() === yesterday.toDateString()) {
        dateKey = 'Yesterday';
      } else {
        dateKey = date.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'short',
          day: 'numeric'
        });
      }

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(activity);
    });

    return groups;
  };

  const handleActivityClick = (activity: ActivityLog) => {
    if (!activity.read) {
      onMarkAsRead(activity.id);
    }
    onSelectActivity(activity);
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner} />
        <span style={styles.loadingText}>Loading activities...</span>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div style={styles.emptyState}>
        <div style={styles.emptyIcon}>
          <Clock size={48} color="#64748b" />
        </div>
        <h3 style={styles.emptyTitle}>No activities yet</h3>
        <p style={styles.emptyText}>
          Client activities will appear here as they interact with the app.
        </p>
      </div>
    );
  }

  const groupedActivities = groupActivitiesByDate(activities);

  return (
    <div style={styles.container}>
      {Object.entries(groupedActivities).map(([dateGroup, groupActivities]) => (
        <div key={dateGroup} style={styles.dateGroup}>
          <div style={styles.dateHeader}>
            <span style={styles.dateLine} />
            <span style={styles.dateLabel}>{dateGroup}</span>
            <span style={styles.dateLine} />
          </div>

          <div style={styles.activitiesList}>
            {groupActivities.map((activity) => (
              <button
                key={activity.id}
                style={{
                  ...styles.activityItem,
                  ...(activity.priority === 'crisis' || activity.priority === 'high'
                    ? styles.activityItemHighPriority
                    : {}),
                  ...(!activity.read ? styles.activityItemUnread : {}),
                  width: '100%',
                  border: 'none',
                  font: 'inherit',
                  color: 'inherit',
                  textAlign: 'left'
                }}
                onClick={() => handleActivityClick(activity)}
                aria-label={`${activity.title} - ${activity.clientName} - ${formatTimestamp(activity.timestamp)}`}
              >
                {/* Priority indicator */}
                <div
                  style={{
                    ...styles.priorityIndicator,
                    backgroundColor: getPriorityBgColor(activity.priority)
                  }}
                >
                  <div style={{ color: getPriorityColor(activity.priority) }}>
                    {getActivityIcon(activity.activityType)}
                  </div>
                </div>

                {/* Content */}
                <div style={styles.activityContent}>
                  <div style={styles.activityHeader}>
                    <span style={styles.clientName}>{activity.clientName}</span>
                    <span style={styles.timestamp}>
                      {formatTimestamp(activity.timestamp)}
                    </span>
                  </div>

                  <div style={styles.activityTitle}>{activity.title}</div>

                  <div style={styles.activityDescription}>
                    {activity.description}
                  </div>

                  {/* Metadata tags */}
                  {activity.metadata && (
                    <div style={styles.metadataTags}>
                      {activity.metadata.sentiment && (
                        <span
                          style={{
                            ...styles.tag,
                            backgroundColor:
                              activity.metadata.sentiment === 'concerning'
                                ? 'rgba(220, 53, 69, 0.1)'
                                : activity.metadata.sentiment === 'negative'
                                ? 'rgba(253, 126, 20, 0.1)'
                                : 'rgba(34, 197, 94, 0.1)',
                            color:
                              activity.metadata.sentiment === 'concerning'
                                ? '#dc3545'
                                : activity.metadata.sentiment === 'negative'
                                ? '#fd7e14'
                                : '#22c55e'
                          }}
                        >
                          {activity.metadata.sentiment}
                        </span>
                      )}
                      {activity.metadata.duration && (
                        <span style={styles.tag}>
                          {activity.metadata.duration} min
                        </span>
                      )}
                      {activity.metadata.messageCount && (
                        <span style={styles.tag}>
                          {activity.metadata.messageCount} messages
                        </span>
                      )}
                      {activity.metadata.score !== undefined && (
                        <span
                          style={{
                            ...styles.tag,
                            backgroundColor:
                              activity.metadata.score >= 15
                                ? 'rgba(220, 53, 69, 0.1)'
                                : activity.metadata.score >= 10
                                ? 'rgba(253, 126, 20, 0.1)'
                                : 'rgba(34, 197, 94, 0.1)',
                            color:
                              activity.metadata.score >= 15
                                ? '#dc3545'
                                : activity.metadata.score >= 10
                                ? '#fd7e14'
                                : '#22c55e'
                          }}
                        >
                          Score: {activity.metadata.score}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Unread indicator */}
                {!activity.read && <div style={styles.unreadDot} />}

                {/* Arrow for clickable items */}
                {activity.metadata?.conversationId && (
                  <div style={styles.viewArrow}>
                    <ChevronRight size={16} color="#64748b" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    padding: '16px'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    gap: '16px'
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #334155',
    borderTopColor: '#3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  loadingText: {
    color: '#64748b',
    fontSize: '14px'
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    textAlign: 'center'
  },
  emptyIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px'
  },
  emptyTitle: {
    margin: '0 0 8px 0',
    fontSize: '18px',
    fontWeight: '600',
    color: '#f8fafc'
  },
  emptyText: {
    margin: 0,
    fontSize: '14px',
    color: '#64748b',
    maxWidth: '300px'
  },
  dateGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  dateHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  dateLine: {
    flex: 1,
    height: '1px',
    backgroundColor: '#334155'
  },
  dateLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap'
  },
  activitiesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  activityItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '14px 16px',
    backgroundColor: '#1e293b',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
    border: '1px solid transparent'
  },
  activityItemHighPriority: {
    borderColor: 'rgba(220, 53, 69, 0.3)',
    backgroundColor: 'rgba(30, 41, 59, 0.9)'
  },
  activityItemUnread: {
    backgroundColor: 'rgba(59, 130, 246, 0.05)',
    borderColor: 'rgba(59, 130, 246, 0.2)'
  },
  priorityIndicator: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  activityContent: {
    flex: 1,
    minWidth: 0
  },
  activityHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px'
  },
  clientName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#f8fafc'
  },
  timestamp: {
    fontSize: '12px',
    color: '#64748b'
  },
  activityTitle: {
    fontSize: '13px',
    color: '#94a3b8',
    marginBottom: '4px'
  },
  activityDescription: {
    fontSize: '12px',
    color: '#64748b',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  metadataTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: '8px'
  },
  tag: {
    fontSize: '11px',
    padding: '2px 8px',
    borderRadius: '4px',
    backgroundColor: 'rgba(100, 116, 139, 0.1)',
    color: '#94a3b8'
  },
  unreadDot: {
    position: 'absolute',
    top: '16px',
    right: '12px',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#3b82f6'
  },
  viewArrow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 'auto'
  }
};

export default ActivityFeed;
